import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { destroyReactComponent } from '@/react-bridge';
import { navigateToMap } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { DATETIME_FORMAT_SERVER_UTC } from '@/constants/formats';
import { getModuleType, hasValue, hasStringValue, getOfficerLabel } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { TourListView } from '@/components/screens/tours/TourListView';
import { TourStopsSidebar } from '@/components/screens/tours/TourStopsSidebar';
import { TourStopDetail } from '@/components/screens/tours/TourStopDetail';

// ============================================================================
// External Declarations
// ============================================================================

import { TourExemption, GuardTourStop, GuardTour, TourStopIssue } from '@/models/legacy/index';
import { getAvailableSubmodules, getOfficerPluralLabel, getCurrentCompanyId, getCurrentUserId, getAuthObject } from '@/utils/auth';
import { isInArray, isInArrayByProperty } from '@/utils/arrays';
const _wg = (name: string) => (...args: any[]) => {
  const fn = (window as any)[name];
  if (typeof fn !== 'function') return undefined;
  return fn(...args);
};
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');
const removeObjectFromArrayByProperty = _wg('removeObjectFromArrayByProperty');
const openWebpage = _wg('openWebpage');
const hasImageExtension = _wg('hasImageExtension');
const updateStatusWithGeolocation = _wg('updateStatusWithGeolocation');
const getGeolocation = _wg('getGeolocation');
const getYouMapMarkerIconPath = _wg('getYouMapMarkerIconPath');
const getGoogleMapsPolygonBounds = _wg('getGoogleMapsPolygonBounds');
const displayKstTimeFromTourTimeZoneToLocalTimeZone = _wg('displayKstTimeFromTourTimeZoneToLocalTimeZone');
const orderBy = _wg('orderBy');

// ============================================================================
// Types
// ============================================================================

enum OfficerCommentsRequired {
  REQUIRED = 1,
  OPTIONAL = 2,
  DISABLED = 3,
}

const OfficerAttachmentsEnabled = {
  ENABLED: true as boolean,
  DISABLED: false as boolean,
};

interface AttachmentInfo {
  id: number | null;
  activationKey: string;
  signedURL?: string;
}

interface Attachment {
  id: number;
  signedURL: string;
  filename: string;
}

interface AttachmentWithGuid {
  signedURL?: string;
}

// ============================================================================
// Module-level State
// ============================================================================

let guardToursContainer: HTMLElement | null = null;
let _isGuardTourStopRealtimeEnabled = false;
let realtimeCompleteGuardTourStopTimeout: ReturnType<typeof setTimeout> | null = null;
let realtimeUpdateTourStopTimeout: ReturnType<typeof setTimeout> | null = null;

// Controller reference for realtime updates from external code
let guardToursNewController: {
  currentStop: any;
  currentTour: any;
  tourStops: any[];
  isCompleted: () => boolean;
  isMissed: () => boolean;
  viewStop: (stopId: number) => void;
  realtimeUpdateStop: (guardTourStopId: number, tourStopId: number) => void;
  realtimeUpdateFromTourStop: (oldTourStopId: number, newTourStopId: number) => void;
  guardTourStopMapMarker: any;
  guardTourStopMapMarkerInterval: any;
} | null = null;

// ============================================================================
// Helper to refresh Font Awesome icons after DOM updates
// ============================================================================

function refreshFontAwesomeIcons(): void {
  setTimeout(() => {
    if ((window as any).FontAwesome && (window as any).FontAwesome.dom) {
      (window as any).FontAwesome.dom.i2svg();
    }
  }, 50);
}

// ============================================================================
// GuardToursScreen Component
// ============================================================================

interface GuardToursScreenProps {
  showAll?: boolean | string;
  guardTourId?: number | null;
  guardTourStopId?: number | null;
}

export function GuardToursScreen({ showAll, guardTourId = null, guardTourStopId = null }: GuardToursScreenProps) {
  const { theme } = useTheme();

  // --- Permissions ---
  const submodules = useMemo(() => getAvailableSubmodules() || [], []);
  const hasUpdateCompletedGuardTourStopRole = useMemo(
    () => isInArray(submodules, AccessAuthorities.ROLE_UPDATE_COMPLETED_GUARD_TOUR_STOP),
    [submodules]
  );
  const officerLabel = useMemo(() => getOfficerLabel(), []);

  // --- View State ---
  const [currentView, setCurrentView] = useState<'list' | 'detail'>('list');
  const [activeTab, setActiveTab] = useState<'claimed' | 'unclaimed' | 'active'>('claimed');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [isLoadingStop, setIsLoadingStop] = useState(false);
  const [claimingTourId, setClaimingTourId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(App.isMobile());

  // --- Tours Data ---
  const [claimedTours, setClaimedTours] = useState<any[]>([]);
  const [unclaimedTours, setUnclaimedTours] = useState<any[]>([]);
  const [activeTours, setActiveTours] = useState<any[]>([]);
  // Enabled tours' schedules (availability blocks + zone), computed client-side
  // into schedule lines and the "Next: ..." empty state. Sourced independently
  // of the guard-tour lists so upcoming tours are known even when out of window.
  const [tourSchedules, setTourSchedules] = useState<any[]>([]);

  // --- Tour Detail ---
  const [currentTour, setCurrentTour] = useState<any>({});
  const [tourStops, setTourStops] = useState<any[]>([]);
  const [stopFilter, setStopFilter] = useState('');
  const [showStopsList, setShowStopsList] = useState(true);

  // --- Stop Detail ---
  const [currentStop, setCurrentStop] = useState<any>(null);
  const [stopTab, setStopTab] = useState('tasks');

  // --- Task/Exception State ---
  const [showingExceptions, setShowingExceptions] = useState(false);
  const [selectedException, setSelectedException] = useState<any>(null);
  const [currentTask, setCurrentTask] = useState<any>(null);
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [isTaskCompletionExemptionMode, setIsTaskCompletionExemptionMode] = useState(false);

  // --- Issue State ---
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [newIssue, setNewIssue] = useState<any>({ title: '', description: '', resizedImageList: [] });

  // --- Map State ---
  const guardTourStopMapMarkerRef = useRef<any>(null);
  const guardTourStopMapMarkerIntervalRef = useRef<any>(null);

  // --- Realtime ---
  const [goBackToMap] = useState(false);

  // --- Refs for stable callbacks ---
  const currentStopRef = useRef(currentStop);
  currentStopRef.current = currentStop;
  const currentTourRef = useRef(currentTour);
  currentTourRef.current = currentTour;
  const tourStopsRef = useRef(tourStops);
  tourStopsRef.current = tourStops;

  // ===== Computed =====

  const hasClaimedTour = (claimedTours ?? []).length > 0;

  const filteredStops = useMemo(() => {
    if (!stopFilter) {
      return orderBy(tourStops, ['listIdx', 'title'], ['asc', 'asc']);
    }
    const filter = stopFilter.toLowerCase();
    return orderBy(
      tourStops.filter(
        (s: any) =>
          s.title.toLowerCase().includes(filter) ||
          s.location.toLowerCase().includes(filter)
      ),
      ['listIdx', 'title'],
      ['asc', 'asc']
    );
  }, [tourStops, stopFilter]);

  const sortedTasks = useMemo(() => {
    if (!currentStop || !currentStop.tourStopTaskList) return [];
    return orderBy(currentStop.tourStopTaskList, ['listIdx', 'name'], ['asc', 'asc']);
  }, [currentStop]);

  const enabledExceptions = useMemo(() => {
    if (isTaskCompletionExemptionMode && currentTask && currentStop) {
      const taskExemptionIds = currentTask.taskCompletionExemptionIds || [];
      return (currentStop.taskCompletionExemptionList || []).filter(
        (e: any) => taskExemptionIds.includes(e.id)
      );
    }
    return exceptions.filter((e: any) => e.isEnabled);
  }, [exceptions, isTaskCompletionExemptionMode, currentTask, currentStop]);

  const isCompleted = useMemo(() => {
    if (!currentStop) return false;
    return currentStop.numTimesToComplete === (currentStop.completionList?.length || 0);
  }, [currentStop]);

  const isMissed = useMemo(() => {
    if (!currentStop) return false;
    const activeEndDateTime = currentStop.activeEndDateTime;
    if (!hasValue(activeEndDateTime)) return false;
    return !isCompleted && moment(activeEndDateTime).isBefore(moment());
  }, [currentStop, isCompleted]);

  const isClaimed = useMemo(() => {
    if (!currentStop) return false;
    return currentStop.claimedByIdList?.length > 0;
  }, [currentStop]);

  const isClaimedByMe = useMemo(() => {
    if (!currentStop) return false;
    const userId = getAuthObject().user_id;
    return currentStop.claimedByIdList?.includes(userId);
  }, [currentStop]);

  const areOfficerCommentsDisabled = useMemo(() => {
    if (!currentStop) return true;
    return currentStop.areOfficerCommentsRequired === OfficerCommentsRequired.DISABLED;
  }, [currentStop]);

  const areOfficerAttachmentsEnabled = useMemo(() => {
    if (!currentStop) return false;
    return currentStop.areOfficerAttachmentsEnabled === OfficerAttachmentsEnabled.ENABLED;
  }, [currentStop]);

  const timeRemaining = useMemo(() => {
    if (!currentTour.MaxEndTime) return null;
    return moment(currentTour.MaxEndTime).fromNow();
  }, [currentTour]);

  const isTimeUrgent = useMemo(() => {
    if (!currentTour.MaxEndTime) return false;
    return moment(currentTour.MaxEndTime).diff(moment(), 'minutes') < 30;
  }, [currentTour]);

  // ===== Handlers =====

  const handleResizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleResize = useCallback(() => {
    if (handleResizeTimerRef.current) clearTimeout(handleResizeTimerRef.current);
    handleResizeTimerRef.current = setTimeout(() => setIsMobile(App.isMobile()), 150);
  }, []);

  const cleanupMap = useCallback(() => {
    if (guardTourStopMapMarkerIntervalRef.current) {
      clearInterval(guardTourStopMapMarkerIntervalRef.current);
      guardTourStopMapMarkerIntervalRef.current = null;
    }
    guardTourStopMapMarkerRef.current = null;
  }, []);

  // --- Load tours ---
  const loadTours = useCallback(
    (showAllTours?: boolean | string, gTourId: number | null = null, gTourStopId: number | null = null) => {
      setIsLoading(true);
      setClaimedTours([]);
      setUnclaimedTours([]);
      setActiveTours([]);

      let autoNavigated = false;

      api.getGuardTourSchedules((scheduleResult: any) => {
        setTourSchedules(scheduleResult?.rows || []);
      });

      api.getOpenGuardTours(1, 50000, (result: any) => {
        const claimed = result.rows || [];
        setClaimedTours(claimed);

        // Auto-navigate to single tour
        if (
          (claimed.length === 1 && showAllTours?.toString() !== 'true') ||
          (claimed.length === 1 && gTourId != null && gTourId.toString() === claimed[0].id.toString())
        ) {
          autoNavigated = true;
          viewTourStops(claimed[0].id, claimed[0], gTourStopId);
        }

        api.getPendingGuardTours(1, 50000, (pendingResult: any) => {
          const unclaimed = pendingResult.rows || [];
          setUnclaimedTours(unclaimed);

          if (!autoNavigated) {
            for (const tour of unclaimed) {
              if (gTourId != null && gTourId.toString() === tour.id.toString()) {
                autoNavigated = true;
                viewTourStops(tour.id, tour, gTourStopId);
                break;
              }
            }
          }

          api.getActiveGuardTours(1, 50000, (activeResult: any) => {
            const active = activeResult.rows || [];
            setActiveTours(active);

            if (!autoNavigated) {
              for (const tour of active) {
                if (gTourId != null && gTourId.toString() === tour.id.toString()) {
                  viewTourStops(tour.id, tour, gTourStopId);
                  break;
                }
              }
            }

            // Set appropriate default tab
            if (claimed.length > 0) {
              setActiveTab('claimed');
            } else if (unclaimed.length > 0) {
              setActiveTab('unclaimed');
            }

            setIsLoading(false);
          });
        });
      });
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const loadExceptions = useCallback(() => {
    api.getTourExemptions(
      `{"data":{"take":30,"skip":0,"page":1,"pageSize":30}}`,
      (result: any) => {
        setExceptions((result.rows || []).map((r: any) => new TourExemption(r)));
      }
    );
  }, []);

  // --- Tour actions ---
  const claimTour = useCallback(
    (tour: any) => {
      setClaimingTourId(tour.id);
      api.claimGuardTour(tour.id, true, () => {
        setClaimingTourId(null);
        loadTours(false);
      });
    },
    [loadTours]
  );

  const unclaimTour = useCallback(
    (tour: any) => {
      setClaimingTourId(tour.id);
      api.claimGuardTour(tour.id, false, () => {
        setClaimingTourId(null);
        loadTours(false);
      });
    },
    [loadTours]
  );

  const claimCurrentTour = useCallback(() => {
    if (currentTourRef.current.id) {
      setClaimingTourId(currentTourRef.current.id);
      api.claimGuardTour(currentTourRef.current.id, true, () => {
        setClaimingTourId(null);
        loadTours(false);
      });
    }
  }, [loadTours]);

  // --- Select/View stop ---
  const selectStop = useCallback(
    (stop: any, skipStatusUpdate = false) => {
      setShowStopsList(false);
      setIsLoadingStop(true);

      api.getGuardTourStop(stop.id, (result: any) => {
        const newStop = new GuardTourStop(result);
        setCurrentStop(newStop);
        setIsLoadingStop(false);

        const isStopCompleted = newStop.completionList && newStop.completionList.length > 0;
        setStopTab(isStopCompleted ? 'completed' : 'tasks');
        setShowAddIssue(false);
        setShowingExceptions(false);
        setGuardTourStopRealtimeEnabled(true);

        // Update global controller
        if (guardToursNewController) {
          guardToursNewController.currentStop = newStop;
        }

        refreshFontAwesomeIcons();

        if (localStorage.getItem('IsStatusUpdateOnTourStopsEnabled') === 'true' && !skipStatusUpdate) {
          updateStatusWithGeolocation('Stop Started');
        }
      });
    },
    []
  );

  const viewStopById = useCallback(
    (stopId: number) => {
      const stop = tourStopsRef.current.find((s: any) => s.id === stopId);
      if (stop) {
        selectStop(stop);
      }
    },
    [selectStop]
  );

  // --- View tour stops ---
  const viewTourStops = useCallback(
    (guardTourIdParam: number, guardTour: any, gTourStopId: number | null = null) => {
      setCurrentView('detail');
      const tour = new GuardTour(guardTour);
      setCurrentTour(tour);
      setTourStops([]);
      setCurrentStop(null);
      setStopTab('tasks');
      setIsLoadingStops(true);

      api.getGuardTourStopsForGuardTour(guardTourIdParam, (result: any) => {
        const stops = result.entries || [];
        setTourStops(stops);
        setIsLoadingStops(false);

        if (stops.length > 0) {
          tour.MinStartTime = moment
            .min(stops.map((n: any) => moment.utc(n.activeStartDateTime)))
            .toString();
          tour.MaxEndTime = moment
            .max(stops.map((n: any) => moment.utc(n.activeEndDateTime)))
            .toString();
          tour.stopsCompleted = stops.filter(
            (s: any) => s.numTimesGuardTourStopCompleted >= s.numTimesToCompleteGuardTourStop
          ).length;
          tour.totalStops = stops.length;
          setCurrentTour({ ...tour });

          const sortedStops = orderBy(stops, ['listIdx', 'title'], ['asc', 'asc']);
          if (gTourStopId) {
            const specificStop = sortedStops.find(
              (s: any) => s.id.toString() === gTourStopId.toString()
            );
            if (specificStop) {
              selectStop(specificStop);
            }
          } else {
            const incompleteStop = sortedStops.find(
              (s: any) => s.numTimesGuardTourStopCompleted < s.numTimesToCompleteGuardTourStop
            );
            selectStop(incompleteStop || sortedStops[0]);
          }
        }

        // Update global controller
        if (guardToursNewController) {
          guardToursNewController.currentTour = tour;
          guardToursNewController.tourStops = stops;
        }

        refreshFontAwesomeIcons();
      });
    },
    [selectStop]
  );

  // --- Refresh tour stops (sidebar) ---
  const refreshTourStops = useCallback(() => {
    const tour = currentTourRef.current;
    if (!tour || !tour.id) return;

    api.getGuardTourStopsForGuardTour(tour.id, (result: any) => {
      const stops = result.entries || [];
      setTourStops(stops);

      if (stops.length > 0) {
        tour.MinStartTime = moment
          .min(stops.map((n: any) => moment.utc(n.activeStartDateTime)))
          .toString();
        tour.MaxEndTime = moment
          .max(stops.map((n: any) => moment.utc(n.activeEndDateTime)))
          .toString();
        tour.stopsCompleted = stops.filter(
          (s: any) => s.numTimesGuardTourStopCompleted >= s.numTimesToCompleteGuardTourStop
        ).length;
        setCurrentTour({ ...tour });
      }

      if (guardToursNewController) {
        guardToursNewController.tourStops = stops;
      }
    });
  }, []);

  // --- Navigation ---
  const goBack = useCallback(() => {
    if (currentView === 'detail') {
      setCurrentView('list');
      setCurrentStop(null);
      cleanupMap();
      setGuardTourStopRealtimeEnabled(false);
      loadTours(true);
    }
  }, [currentView, cleanupMap, loadTours]);

  // --- Task methods ---
  const toggleTask = useCallback(
    (task: any) => {
      if (isCompleted || isMissed || !isClaimedByMe) return;
      if (!isTimeSensitiveEnabled(task.timeSensitive)) return;

      if (
        !task.isChecked &&
        task.timeSensitive &&
        moment().isAfter(task.timeSensitive.endDateTime, 'minutes')
      ) {
        setCurrentTask(task);
        task.isChecked = true;
        setCurrentStop((prev: any) => ({ ...prev })); // trigger re-render
        setShowingExceptions(true);
        return;
      }

      const newCheckedState = !task.isChecked;
      task.isChecked = newCheckedState;

      if (!newCheckedState) {
        task.exceptionId = null;
        task.tourExemption = null;
        task.isTaskCompletionExemption = false;
      }

      setCurrentStop((prev: any) => ({ ...prev })); // trigger re-render
      completeTask(task);
    },
    [isCompleted, isMissed, isClaimedByMe]
  );

  const completeTask = useCallback(
    (task: any) => {
      const completedTask: any = {
        guardTourStopId: currentStopRef.current.id,
        tourStopTaskId: task.tourStopTaskId,
        isCompleted: task.isChecked,
        exemptionRevisionGroup: task.exceptionId || null,
      };
      if (task.isTaskCompletionExemption) {
        completedTask.isTaskCompletionExemption = true;
      }
      api.completeTask(JSON.stringify(completedTask), () => {});
    },
    []
  );

  const saveException = useCallback(() => {
    if (!selectedException || !currentTask) return;
    currentTask.exceptionId = selectedException.revisionGroup;
    currentTask.tourExemption = selectedException;
    if (isTaskCompletionExemptionMode) {
      currentTask.isTaskCompletionExemption = true;
    }
    completeTask(currentTask);
    setShowingExceptions(false);
    setSelectedException(null);
    setCurrentTask(null);
    setIsTaskCompletionExemptionMode(false);
    setCurrentStop((prev: any) => ({ ...prev }));
  }, [selectedException, currentTask, completeTask, isTaskCompletionExemptionMode]);

  const cancelException = useCallback(() => {
    if (currentTask) {
      currentTask.isChecked = false;
      setCurrentStop((prev: any) => ({ ...prev }));
    }
    setShowingExceptions(false);
    setSelectedException(null);
    setCurrentTask(null);
    setIsTaskCompletionExemptionMode(false);
  }, [currentTask]);

  const showTaskExemptions = useCallback(
    (task: any) => {
      if (isCompleted || isMissed || !isClaimedByMe) return;
      setCurrentTask(task);
      task.isChecked = true;
      setCurrentStop((prev: any) => ({ ...prev }));
      setIsTaskCompletionExemptionMode(true);
      setShowingExceptions(true);
    },
    [isCompleted, isMissed, isClaimedByMe]
  );

  // --- Checklist methods ---
  const toggleChecklistItem = useCallback(
    (item: any) => {
      if (isCompleted) return;
      item.isChecked = !item.isChecked;
      item.completedAt = item.isChecked ? new Date() : null;
      setCurrentStop((prev: any) => ({ ...prev }));
    },
    [isCompleted]
  );

  // --- Issue methods ---
  const addIssue = useCallback(() => {
    setShowAddIssue(true);
    setNewIssue({ title: '', description: '', resizedImageList: [], newAttachmentInfoList: [] });
  }, []);

  const cancelAddIssue = useCallback(() => {
    setShowAddIssue(false);
    setNewIssue({ title: '', description: '', resizedImageList: [] });
  }, []);

  const saveIssue = useCallback(() => {
    if (!newIssue.title?.trim() || !newIssue.description?.trim()) {
      App.showPopupNotification('Please add both title and description to issue', 'error');
      return;
    }

    const issue = new TourStopIssue();
    issue.title = newIssue.title;
    issue.description = newIssue.description;
    issue.resizedImageList = newIssue.resizedImageList;
    issue.newAttachmentInfoList = newIssue.newAttachmentInfoList || [];

    setCurrentStop((prev: any) => {
      const updated = { ...prev };
      updated.tourStopIssues = [...(updated.tourStopIssues || []), issue];
      return updated;
    });
    setShowAddIssue(false);
    setNewIssue({ title: '', description: '', resizedImageList: [] });
  }, [newIssue]);

  const uploadIssuePhoto = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, photoIndex: number) => {
      const fileInput = event.target;

      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, info: AttachmentInfo) => {
          setNewIssue((prev: any) => {
            const updated = { ...prev };
            if (!updated.newAttachmentInfoList) {
              updated.newAttachmentInfoList = [];
            }
            updated.newAttachmentInfoList = [...updated.newAttachmentInfoList, info];
            return updated;
          });
        },
        successCallback: (_idx: number, att: Attachment) => {
          setNewIssue((prev: any) => {
            const updated = { ...prev };
            const list = [...(updated.resizedImageList || [])];
            list[photoIndex] = att;
            updated.resizedImageList = list;
            return updated;
          });
        },
      });
    },
    []
  );

  const uploadStopAttachment = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileInput = event.target;

      App.uploadFromFileInput(fileInput, {
        updateAttachmentInfo: (_idx: number, info: AttachmentInfo) => {
          setCurrentStop((prev: any) => {
            const updated = { ...prev };
            if (!updated.newAttachmentInfoList) {
              updated.newAttachmentInfoList = [];
            }
            updated.newAttachmentInfoList = [...updated.newAttachmentInfoList, info];
            return updated;
          });
        },
        successCallback: (_idx: number, att: Attachment) => {
          setCurrentStop((prev: any) => {
            const updated = { ...prev };
            updated.attachmentList = [...(updated.attachmentList || []), att];

            if (hasImageExtension(att.filename)) {
              updated.resizedImageList = [...(updated.resizedImageList || []), att];
            }
            return updated;
          });
        },
      });
    },
    []
  );

  // --- Attachment methods ---
  const showAttachment = useCallback((attachment: any, completedStop: any = null) => {
    const list = completedStop ? completedStop.attachmentList : currentStopRef.current?.attachmentList;
    const att = getObjectInArrayByProperty(list, 'id', attachment.id);
    if (att?.signedURL) openWebpage(att.signedURL);
  }, []);

  const removeAttachment = useCallback((id: number) => {
    setCurrentStop((prev: any) => {
      const updated = { ...prev };
      updated.resizedImageList = (updated.resizedImageList || []).filter((a: any) => a.id !== id);
      updated.attachmentList = (updated.attachmentList || []).filter((a: any) => a.id !== id);
      updated.newAttachmentInfoList = (updated.newAttachmentInfoList || []).filter((a: any) => a.id !== id);
      return updated;
    });
  }, []);

  const getResizedImage = useCallback((id: number, completedStop: any = null) => {
    const list = completedStop
      ? completedStop.resizedImageList
      : currentStopRef.current?.resizedImageList;
    const img = getObjectInArrayByProperty(list, 'id', id);
    return img?.signedURL || '';
  }, []);

  const isResizedImageAvailable = useCallback((id: number, completedStop: any = null) => {
    const list = completedStop
      ? completedStop.resizedImageList
      : currentStopRef.current?.resizedImageList;
    return isInArrayByProperty(list, 'id', id);
  }, []);

  const isImageAttachment = useCallback((attachment: any) => {
    return hasImageExtension(attachment.filename);
  }, []);

  // --- Complete stop ---
  const completeStop = useCallback(() => {
    const stop = currentStopRef.current;
    if (!stop) return;

    // Validate checklist
    if (isInArrayByProperty(stop.tourStopChecklist, 'completedAt', null)) {
      App.showPopupNotification('Checklist must be complete.', 'error');
      return;
    }

    // Validate officer comments
    if (
      stop.areOfficerCommentsRequired === OfficerCommentsRequired.REQUIRED &&
      !hasStringValue(stop.officerComments)
    ) {
      App.showPopupNotification(`${officerLabel} comments are required.`, 'error');
      return;
    }

    // Check if already ended
    if (moment(stop.activeEndDateTime).isBefore(moment())) {
      App.showPopupNotification(
        'This tour or tour stop has reached its end time and can no longer be completed.',
        'error'
      );
      return;
    }

    // Prepare data
    const tourStopChecklistData = (stop.tourStopChecklist || []).map((item: any) => ({
      id: item.id,
      completedAt: moment(item.completedAt).utc().format(DATETIME_FORMAT_SERVER_UTC),
    }));

    const tourStopIssuesData = (stop.tourStopIssues || []).map((issue: any) => {
      const issueData: any = {
        id: issue.id,
        title: issue.title,
        description: issue.description,
        isResolved: issue.isResolved,
        newAttachmentInfoList: (issue.newAttachmentInfoList || []).map((info: any) => {
          const { signedURL, ...rest } = info;
          return rest;
        }),
      };
      return issueData;
    });

    setGuardTourStopRealtimeEnabled(false);

    const submitTourStopCompletion = (latitude: number, longitude: number) => {
      const completeData = {
        officerComments: stop.officerComments,
        latitude: latitude,
        longitude: longitude,
        tourStopChecklist: tourStopChecklistData,
        tourStopIssues: tourStopIssuesData,
        newAttachmentInfoList: stop.newAttachmentInfoList || [],
      };

      api.completeGuardTourStop(
        stop.id,
        JSON.stringify(completeData),
        () => {
          // Success
          if (goBackToMap) {
            navigateToMap();
          } else {
            const needsMoreCompletions =
              (stop.completionList?.length || 0) + 1 < stop.numTimesToComplete + 1;

            setGuardTourStopRealtimeEnabled(true);

            if (needsMoreCompletions) {
              selectStop(stop, true);
              refreshTourStops();
            } else {
              goBack();
            }
          }

          if (localStorage.getItem('IsStatusUpdateOnTourStopsEnabled') === 'true') {
            api.statusUpdate(
              {
                statusComment: 'Stop Completed',
                latitude: latitude,
                longitude: longitude,
              },
              () => {},
              () => App.showPopupNotification('Unable to update status', 'error')
            );
          }
        },
        () => {
          App.showPopupNotification('Tour stop failed to complete.', 'error');
          setGuardTourStopRealtimeEnabled(true);
        }
      );
    };

    // User 10722 diagnostic carve-out: complete with (0,0) immediately and log
    // geolocation behavior in the background so the prompt/timeout cycle doesn't
    // block tour stop completion while we investigate.
    if (getCurrentUserId() === 10722) {
      submitTourStopCompletion(0, 0);

      const JSErrorType = (window as any).JSErrorType;
      const nav: any = navigator;
      if (nav.permissions && typeof nav.permissions.query === 'function') {
        nav.permissions.query({ name: 'geolocation' })
          .then((p: any) => {
            api.insertNewJSError(
              `[geo-diag-10722] permission.state=${p?.state}`,
              null, null, JSErrorType.CAUGHT, null
            );
          })
          .catch((e: any) => {
            api.insertNewJSError(
              `[geo-diag-10722] permission.query failed: ${e?.message}`,
              null, null, JSErrorType.CAUGHT, null
            );
          });
      } else {
        api.insertNewJSError(
          '[geo-diag-10722] navigator.permissions.query unavailable',
          null, null, JSErrorType.CAUGHT, null
        );
      }

      getGeolocation(
        (position: GeolocationPosition) => {
          api.insertNewJSError(
            `[geo-diag-10722] got position lat=${position.coords.latitude} lng=${position.coords.longitude} accuracy=${position.coords.accuracy}`,
            null, null, JSErrorType.CAUGHT, null
          );
        },
        (err: GeolocationPositionError) => {
          api.insertNewJSError(
            `[geo-diag-10722] getGeolocation failed code=${err?.code} message=${err?.message}`,
            null, null, JSErrorType.CAUGHT, null
          );
        }
      );
      return;
    }

    getGeolocation(
      (position: GeolocationPosition) => {
        submitTourStopCompletion(position.coords.latitude, position.coords.longitude);
      },
      () => {
        const companyId = getCurrentCompanyId();
        if ((companyId === 816 || companyId === 928) && App.getModuleTypeFromURL() === 'cad') {
          submitTourStopCompletion(0, 0);
        } else {
          App.showPopupNotification(
            'Unable to get your location to complete the tour stop.',
            'error'
          );
          setGuardTourStopRealtimeEnabled(true);
        }
      }
    );
  }, [officerLabel, goBackToMap, selectStop, refreshTourStops, goBack]);

  const updateCompletedComment = useCallback((completedStop: any) => {
    api.updateCompleteGuardStop(completedStop.id, completedStop.officerComments, () => {});
  }, []);

  // --- GeoFence map ---
  const showGeoFence = useCallback(() => {
    const geofence = currentStopRef.current?.geofence;
    if (!geofence) return;

    const updateMap = (mapElt: HTMLElement) => {
      const map = new (window as any).google.maps.Map(mapElt, {
        zoom: 13,
        center: { lat: 0, lng: 0 },
      });

      const coords = geofence.slice(0, geofence.length - 1);
      const polygon = new (window as any).google.maps.Polygon({
        paths: coords,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        editable: false,
      });
      polygon.setMap(map);
      map.fitBounds(getGoogleMapsPolygonBounds(polygon));

      getGeolocation(
        (position) => {
          const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
          const markerIcon = {
            url: getYouMapMarkerIconPath(),
            anchor: new (window as any).google.maps.Point(10, 15),
          };
          guardTourStopMapMarkerRef.current = new (window as any).google.maps.Marker({
            map: map,
            title: 'You',
            position: pos,
            icon: markerIcon,
          });
        },
        () => {}
      );

      guardTourStopMapMarkerIntervalRef.current = setInterval(() => {
        if (!guardTourStopMapMarkerRef.current) {
          cleanupMap();
          return;
        }
        getGeolocation(
          (position) => {
            if (guardTourStopMapMarkerRef.current) {
              guardTourStopMapMarkerRef.current.setPosition({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            }
          },
          () => {}
        );
      }, 4000);
    };

    setTimeout(() => {
      const mapElt = document.getElementById('map');
      if (mapElt) {
        updateMap(mapElt);
      } else {
        setTimeout(() => {
          const mapElt2 = document.getElementById('map');
          if (mapElt2) updateMap(mapElt2);
        }, 500);
      }
    }, 100);
  }, [cleanupMap]);

  // --- Format helpers ---
  function isTimeSensitiveEnabled(timeSensitive: any): boolean {
    if (!timeSensitive) return true;
    return moment().isAfter(timeSensitive.startDateTime, 'minutes');
  }

  // --- Realtime update methods ---
  const realtimeUpdateStop = useCallback(
    (guardTourStopIdParam: number, tourStopId: number) => {
      const stop = currentStopRef.current;
      if (stop?.id === guardTourStopIdParam) {
        api.getGuardTourStop(guardTourStopIdParam, (result: any) => {
          setCurrentStop((prev: any) => {
            if (!prev) return prev;
            const updated = { ...prev };
            updated.tourStopIssues = getTourStopIssuesFromResult(result);
            updated.officerComments = result.officerComments;
            updated.latitude = result.latitude;
            updated.longitude = result.longitude;
            return updated;
          });
          refreshFontAwesomeIcons();
        });
      } else if (stop?.tourStopId === tourStopId) {
        api.getGuardTourStop(guardTourStopIdParam, (result: any) => {
          setCurrentStop((prev: any) => {
            if (!prev) return prev;
            const updated = { ...prev };
            if (prev.numTimesToComplete === (prev.completionList?.length || 0)) {
              updated.tourStopIssues = getTourStopIssuesFromResult(result);
            } else {
              updated.tourStopIssues = mergeTourStopIssuesFromResult(result, prev.tourStopIssues);
            }
            return updated;
          });
          refreshFontAwesomeIcons();
        });
      }
    },
    []
  );

  const realtimeUpdateFromTourStop = useCallback(
    (oldTourStopId: number, _newTourStopId: number) => {
      const stop = currentStopRef.current;
      if (stop?.tourStopId === oldTourStopId) {
        const isStopCompleted = stop.numTimesToComplete === (stop.completionList?.length || 0);
        const isStopMissed = (() => {
          if (!hasValue(stop.activeEndDateTime)) return false;
          return !isStopCompleted && moment(stop.activeEndDateTime).isBefore(moment());
        })();
        if (!isStopCompleted && !isStopMissed) {
          selectStop(stop, true);
        }
      }
    },
    [selectStop]
  );

  // --- Setup effects ---

  // Mount: load tours, exceptions, set up global controller, event listeners
  useEffect(() => {
    loadTours(showAll, guardTourId, guardTourStopId);
    loadExceptions();
    window.addEventListener('resize', handleResize);
    refreshFontAwesomeIcons();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (handleResizeTimerRef.current) clearTimeout(handleResizeTimerRef.current);
      cleanupMap();
      guardToursNewController = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the global controller updated with current state
  useEffect(() => {
    guardToursNewController = {
      currentStop: currentStop,
      currentTour: currentTour,
      tourStops: tourStops,
      isCompleted: () => isCompleted,
      isMissed: () => isMissed,
      viewStop: viewStopById,
      realtimeUpdateStop: realtimeUpdateStop,
      realtimeUpdateFromTourStop: realtimeUpdateFromTourStop,
      guardTourStopMapMarker: guardTourStopMapMarkerRef.current,
      guardTourStopMapMarkerInterval: guardTourStopMapMarkerIntervalRef.current,
    };
  }, [
    currentStop,
    currentTour,
    tourStops,
    isCompleted,
    isMissed,
    viewStopById,
    realtimeUpdateStop,
    realtimeUpdateFromTourStop,
  ]);

  // ===== Render =====
  return (
    <div className={`guard-tours-screen ${theme}`.trim()}>
      {/* ===== Tour List View ===== */}
      {currentView === 'list' && (
        <TourListView
          isLoading={isLoading}
          activeTab={activeTab}
          claimedTours={claimedTours}
          unclaimedTours={unclaimedTours}
          activeTours={activeTours}
          tourSchedules={tourSchedules}
          hasClaimedTour={hasClaimedTour}
          claimingTourId={claimingTourId}
          onSetActiveTab={setActiveTab}
          onViewTourStops={viewTourStops}
          onClaimTour={claimTour}
          onUnclaimTour={unclaimTour}
        />
      )}

      {/* ===== Tour Detail View ===== */}
      {currentView === 'detail' && (
        <>
          <div className="header">
            <h1>{currentTour.title}</h1>
            <div className="header-actions">
              <button className="btn-back" onClick={goBack}>
                <i className="fa fa-arrow-left"></i> Back
              </button>
            </div>
          </div>

          <div className="tour-detail-layout">
            <TourStopsSidebar
              currentTour={currentTour}
              currentStop={currentStop}
              filteredStops={filteredStops}
              stopFilter={stopFilter}
              isLoadingStops={isLoadingStops}
              isMobile={isMobile}
              showStopsList={showStopsList}
              timeRemaining={timeRemaining}
              isTimeUrgent={isTimeUrgent}
              onSetStopFilter={setStopFilter}
              onSelectStop={selectStop}
            />
            <TourStopDetail
              currentStop={currentStop}
              stopTab={stopTab}
              isMobile={isMobile}
              showStopsList={showStopsList}
              isLoadingStop={isLoadingStop}
              isCompleted={isCompleted}
              isMissed={isMissed}
              isClaimedByMe={isClaimedByMe}
              areOfficerCommentsDisabled={areOfficerCommentsDisabled}
              areOfficerAttachmentsEnabled={areOfficerAttachmentsEnabled}
              hasUpdateCompletedGuardTourStopRole={hasUpdateCompletedGuardTourStopRole}
              hasClaimedTour={hasClaimedTour}
              isClaimingTour={claimingTourId != null}
              sortedTasks={sortedTasks}
              enabledExceptions={enabledExceptions}
              showingExceptions={showingExceptions}
              selectedException={selectedException}
              showAddIssue={showAddIssue}
              newIssue={newIssue}
              onSetStopTab={setStopTab}
              onSetShowStopsList={setShowStopsList}
              onToggleTask={toggleTask}
              onShowTaskExemptions={showTaskExemptions}
              isTaskCompletionExemptionMode={isTaskCompletionExemptionMode}
              onToggleChecklistItem={toggleChecklistItem}
              onSetSelectedException={setSelectedException}
              onSaveException={saveException}
              onCancelException={cancelException}
              onAddIssue={addIssue}
              onCancelAddIssue={cancelAddIssue}
              onSaveIssue={saveIssue}
              onSetNewIssue={setNewIssue}
              onUploadIssuePhoto={uploadIssuePhoto}
              onUploadStopAttachment={uploadStopAttachment}
              onShowAttachment={showAttachment}
              onRemoveAttachment={removeAttachment}
              onGetResizedImage={getResizedImage}
              onIsResizedImageAvailable={isResizedImageAvailable}
              onIsImageAttachment={isImageAttachment}
              onShowGeoFence={showGeoFence}
              onSetCurrentStop={setCurrentStop}
              onCompleteStop={completeStop}
              onClaimCurrentTour={claimCurrentTour}
              onUpdateCompletedComment={updateCompletedComment}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Helper Functions (used by realtime updates)
// ============================================================================

function getTourStopIssuesFromResult(result: any): any[] {
  const issues: any[] = [];
  for (const issueResult of result.tourStopIssues || []) {
    const issue = new TourStopIssue(issueResult);
    while (issue.resizedImageList.length < 2) {
      issue.resizedImageList.push({});
    }
    issues.push(issue);
  }
  return issues;
}

function mergeTourStopIssuesFromResult(result: any, currentIssues: any[]): any[] {
  const latestIssues = getTourStopIssuesFromResult(result);
  const issueIdToResolved: Record<string, any> = {};

  for (const issue of currentIssues || []) {
    if (issue.id) {
      issueIdToResolved[issue.id.toString()] = issue.isResolved;
    }
  }

  for (const latest of latestIssues) {
    if (issueIdToResolved[latest.id.toString()]?.isResolved) {
      latest.isResolved = true;
    }
  }

  for (const issue of currentIssues || []) {
    if (!issue.id) {
      latestIssues.push(issue);
    }
  }

  return latestIssues;
}

// ============================================================================
// Realtime Enable/Disable
// ============================================================================

function isGuardTourStopRealtimeEnabled(): boolean {
  return _isGuardTourStopRealtimeEnabled;
}

function setGuardTourStopRealtimeEnabled(isEnabled: boolean): void {
  if (isEnabled) {
    setTimeout(() => {
      _isGuardTourStopRealtimeEnabled = true;
    }, 0);
  } else {
    _isGuardTourStopRealtimeEnabled = false;
  }
}

// ============================================================================
// Imperative Destroy Function
// ============================================================================

function destroyGuardToursScreen(): void {
  // Clean up realtime timeouts
  if (realtimeCompleteGuardTourStopTimeout) {
    clearTimeout(realtimeCompleteGuardTourStopTimeout);
    realtimeCompleteGuardTourStopTimeout = null;
  }
  if (realtimeUpdateTourStopTimeout) {
    clearTimeout(realtimeUpdateTourStopTimeout);
    realtimeUpdateTourStopTimeout = null;
  }
  setGuardTourStopRealtimeEnabled(false);

  if (guardToursNewController) {
    if (guardToursNewController.guardTourStopMapMarkerInterval) {
      clearInterval(guardToursNewController.guardTourStopMapMarkerInterval);
      guardToursNewController.guardTourStopMapMarkerInterval = null;
    }
    guardToursNewController.guardTourStopMapMarker = null;
  }

  if (guardToursContainer) {
    destroyReactComponent(guardToursContainer);
    const parent = guardToursContainer.parentNode;
    if (parent) {
      const placeholder = document.createElement('div');
      placeholder.id = 'guardToursScreen';
      parent.replaceChild(placeholder, guardToursContainer);
    }
    guardToursContainer = null;
  }
  guardToursNewController = null;
}

// ============================================================================
// Realtime Update Functions (kept for compatibility)
// ============================================================================

function realtimeCompleteGuardTourStop(guardTourStopIdParam: number, tourStopId: number): void {
  if (guardToursNewController) {
    checkRealtimeCompleteGuardTourStop(guardTourStopIdParam, tourStopId);
  }
}

function checkRealtimeCompleteGuardTourStop(guardTourStopIdParam: number, tourStopId: number): void {
  if (isGuardTourStopRealtimeEnabled()) {
    if (realtimeCompleteGuardTourStopTimeout) {
      clearTimeout(realtimeCompleteGuardTourStopTimeout);
      realtimeCompleteGuardTourStopTimeout = null;
    }
    guardToursNewController?.realtimeUpdateStop(guardTourStopIdParam, tourStopId);
  } else {
    realtimeCompleteGuardTourStopTimeout = setTimeout(
      () => checkRealtimeCompleteGuardTourStop(guardTourStopIdParam, tourStopId),
      500
    );
  }
}

function realtimeUpdateTourStop(oldTourStopId: number, newTourStopId: number): void {
  if (guardToursNewController) {
    checkRealtimeUpdateTourStop(oldTourStopId, newTourStopId);
  }
}

function checkRealtimeUpdateTourStop(oldTourStopId: number, newTourStopId: number): void {
  if (isGuardTourStopRealtimeEnabled()) {
    if (realtimeUpdateTourStopTimeout) {
      clearTimeout(realtimeUpdateTourStopTimeout);
      realtimeUpdateTourStopTimeout = null;
    }
    guardToursNewController?.realtimeUpdateFromTourStop(oldTourStopId, newTourStopId);
  } else {
    realtimeUpdateTourStopTimeout = setTimeout(
      () => checkRealtimeUpdateTourStop(oldTourStopId, newTourStopId),
      500
    );
  }
}

function resetCurrentGuardTourStop(): void {
  destroyGuardToursScreen();
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).realtimeCompleteGuardTourStop = realtimeCompleteGuardTourStop;
(window as any).realtimeUpdateTourStop = realtimeUpdateTourStop;
(window as any).resetCurrentGuardTourStop = resetCurrentGuardTourStop;

// ============================================================================
// Screen Registry
// ============================================================================


// ============================================================================
// Exports
// ============================================================================

export {
  realtimeCompleteGuardTourStop,
  realtimeUpdateTourStop,
  resetCurrentGuardTourStop,
};
